import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Popup from '../../../../../../Utilities/components/Popup';

import {
  getPaymentMethodById,
  updatePaymentMethod,
  deletePaymentMethod,
  setDefaultPaymentMethod
} from '../../../../../../Utilities/api';

import { StateContext } from '../../../../../../StateProvider';

import {
  WhiteBackButton,
  DeleteButton
} from '../../../../../../Utilities/components/Buttons';

import Footer from '../../../../../../Footer';
import './EditPaymentMethod.css';

export default function EditPaymentMethod() {
  const params = useParams();
  const id = params ? params.id : null;
  const navigate = useNavigate();

  const {
    user,
    refreshUserData,
    loading,
    setLoading
  } = useContext(StateContext);

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [cardholderName, setCardholderName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState(null);

  // Validate Stripe ID without startsWith()
  function isValidStripeId(value) {
    if (!value) return false;
    if (value.length < 3) return false;

    var prefix = value.substring(0, 3); // old school
    if (prefix === "pm_") return true;

    return false;
  }

  var validId = isValidStripeId(id);

  // Load selected payment method
  useEffect(function () {
    async function fetchData() {
      if (!id || !validId) return;

      try {
        const method = await getPaymentMethodById(id);

        if (!method) {
          navigate('/profile/wallet/payment-methods');
          return;
        }

        setPaymentMethod(method);

        if (method && method.cardholderName) {
          setCardholderName(method.cardholderName);
        }

        // Compare IDs manually (no chain)
        if (user && user.defaultPaymentMethodId && method) {
          var userId = String(user.defaultPaymentMethodId);
          var methodId = String(method.stripePaymentMethodId);

          if (userId === methodId) {
            setIsDefault(true);
          } else {
            setIsDefault(false);
          }
        }

      } catch (err) {
        console.error("Failed to fetch payment method:", err);
        navigate('/profile/wallet/payment-methods');
      }
    }

    fetchData();
  }, [id, validId, user ? user.defaultPaymentMethodId : null, navigate]);

  // Format expiry manually (no padStart, no slice)
  function formatTwoDigits(value) {
    var str = String(value);
    if (str.length === 1) return "0" + str;
    return str;
  }

  function lastTwoDigits(value) {
    var str = String(value);
    if (str.length <= 2) return str;
    return str.substring(str.length - 2);
  }

  // Save updated name + default toggle
  async function handleSave(e) {
    e.preventDefault();

    if (!paymentMethod || !user) return;

    if (!user.stripeCustomerId) {
      setError("Your payment profile is not fully set up. Please add a card first.");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await updatePaymentMethod(user.uid, {
        paymentMethodId: paymentMethod.stripePaymentMethodId,
        stripeCustomerId: user.stripeCustomerId,
        cardholderName: cardholderName
      });

      var pmId = String(paymentMethod.stripePaymentMethodId);

      if (isDefault) {
        await setDefaultPaymentMethod(user.uid, pmId);
      } else {
        var currentDefault = user.defaultPaymentMethodId
          ? String(user.defaultPaymentMethodId)
          : "";

        if (currentDefault === pmId) {
          await setDefaultPaymentMethod(user.uid, null);
        }
      }

      await refreshUserData(user.uid);
      navigate(-1);

    } catch (err) {
      console.error("Failed to update payment method:", err);
      setError("Failed to save changes. Please try again.");
    }

    setLoading(false);
  }

  // Delete + assign new default if needed
  async function handlePopupDelete() {
    if (!paymentMethod || !user) return;

    var wasDefault = false;

    var currentDefault = user.defaultPaymentMethodId
      ? String(user.defaultPaymentMethodId)
      : "";

    var deletingId = String(paymentMethod.stripePaymentMethodId);

    if (currentDefault === deletingId) {
      wasDefault = true;
    }

    try {
      await deletePaymentMethod(deletingId);

      // 1. Refresh BEFORE navigating
      await refreshUserData(user.uid);

      // 2. Assign new default if needed
      if (wasDefault) {
        var methods = [];
        if (user && user.paymentMethods) {
          methods = user.paymentMethods;
        }

        if (methods.length > 0) {
          var first = methods[0];
          if (first && first.stripePaymentMethodId) {
            await setDefaultPaymentMethod(user.uid, first.stripePaymentMethodId);
          }
        } else {
          await setDefaultPaymentMethod(user.uid, null);
        }

        await refreshUserData(user.uid);
      }

      // 3. NOW navigate — safe
      navigate('/profile/wallet');

    } catch (err) {
      console.error("Failed to delete payment method:", err);
    }

    setShowPopup(false);
  }

  var loaded = paymentMethod !== null;

  return (
    <>
      <div className="edit-payment-backdrop">
        <div className="container">

          <Popup
            isOpen={showPopup}
            handleClose={function () {
              setShowPopup(false);
            }}
          >
            <p className="payment-edit-popup-header">Delete Payment Method?</p>
            <div className="payment-edit-popup-button-container">
              <button
                onClick={function () {
                  setShowPopup(false);
                }}
                className="payment-edit-popup-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handlePopupDelete}
                className="payment-edit-popup-delete"
              >
                Delete
              </button>
            </div>
          </Popup>

          <div className="payment-edit-button-container">
            <WhiteBackButton />
            <DeleteButton
              onDelete={function () {
                setShowPopup(true);
              }}
            />
          </div>

          <div className="edit-payment-method">
            <h2 className="edit-payment-header">Update Payment Method</h2>

            {!loaded ? (
              <p>Loading...</p>
            ) : (
              <>
                <div className="edit-payment-info">
                  <p className="edit-payment-info-header">Card Info</p>
                  <hr className="edit-payment-breakline" />

                  <p className="edit-payment-card">
                    Card Ending: {paymentMethod.cardNumber}
                  </p>

                  <p className="edit-payment-cardType">
                    Type: 
                    {paymentMethod.cardType
                      ? paymentMethod.cardType.toUpperCase()
                      : ""}
                  </p>

                  <p className="edit-payment-expiry">
                    Expires: 
                    {paymentMethod.expiryMonth
                      ? formatTwoDigits(paymentMethod.expiryMonth)
                      : ""}
                    /
                    {paymentMethod.expiryYear
                      ? lastTwoDigits(paymentMethod.expiryYear)
                      : ""}
                  </p>
                </div>

                <form>
                  <label htmlFor="cardholderName">Cardholder Name</label>
                  <input
                    id="cardholderName"
                    type="text"
                    value={cardholderName}
                    onChange={function (e) {
                      setCardholderName(e.target.value);
                    }}
                    placeholder="Enter Here"
                  />
                </form>

                {error && <p className="error-message">{error}</p>}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="edit-payment-method-submit-container">
        {loaded ? (
          <div className="default-payment-method">
            <label>
              <input
                type="checkbox"
                checked={isDefault}
                onChange={function (e) {
                  setIsDefault(e.target.checked);
                }}
              />
              Set as Default
            </label>
          </div>
        ) : null}

        <button
          onClick={handleSave}
          className="edit-payment-method-submit"
          disabled={loading || !loaded}
        >
          {loading ? 'Processing...' : 'Save'}
        </button>
      </div>

      <Footer />
    </>
  );
}
